import {
  getDietaryIngredients,
  getWarningText,
  useCustomerDietaryRestrictions,
  type DietaryRestrictionsText,
} from '@hungryroot/dietary-restrictions';
import { WarningTextStyleVariant } from '@hungryroot/pho';
import { PeriodicityTypes, type ProductDetail } from '@hungryroot/rest';
import { useCustomerPeriodicities } from '@hungryroot/rest-hooks';

export const getItemWarning = (
  dietaryProductIngredients: DietaryRestrictionsText[],
  periodicity: PeriodicityTypes | null
) => {
  const containsDietaryIngredients = dietaryProductIngredients.length > 0;
  const isNeverItem = periodicity === PeriodicityTypes.NEVER;

  if (containsDietaryIngredients) {
    return {
      warningText: `This item contains ${getWarningText(
        dietaryProductIngredients
      )}`,
      warningTextIntensity: WarningTextStyleVariant.Critical,
    };
  } else if (isNeverItem) {
    return {
      warningText: `You set this item to 'never'`,
      warningTextIntensity: WarningTextStyleVariant.Moderate,
    };
  }
  return null;
};

function useDietaryRestrictionText(product: ProductDetail | undefined) {
  const dietaryRestrictions = useCustomerDietaryRestrictions();
  const dietaryRestrictionsData =
    dietaryRestrictions.data?.dietary_restrictions;

  const cannotReturnData =
    !dietaryRestrictionsData || product?.tags === undefined;

  if (cannotReturnData) return undefined;
  const dietaryIngredients = getDietaryIngredients(
    product.tags,
    dietaryRestrictionsData
  );

  return dietaryIngredients;
}

function useProductPeriodicity(productId: number | undefined) {
  const cannotReturnData = productId === undefined;
  const periodicityResponse = useCustomerPeriodicities();

  if (cannotReturnData) return undefined;
  const periodicityDataForTargetProduct =
    periodicityResponse?.data?.results.find(
      (entry) => entry.product === productId
    );

  return periodicityDataForTargetProduct?.periodicity ?? null;
}

export function useItemWarning(product: ProductDetail | undefined) {
  const periodicity = useProductPeriodicity(product?.id);

  const dietaryIngredients = useDietaryRestrictionText(product);

  const cannotReturnData =
    dietaryIngredients === undefined || periodicity === undefined;

  if (cannotReturnData) return null;
  return getItemWarning(dietaryIngredients, periodicity);
}
